/* eslint-disable @typescript-eslint/no-use-before-define */
import Cookies from 'js-cookie';
import type { Ref, WritableComputedRef } from 'vue';
import { createI18n } from 'vue-i18n';
import en from '@/assets/i18n/en.json';
import { LanguageType } from '@/interface/enum';
import { useApp } from '@/libraries/composables/useApp';
import { getHost } from '@/libraries/url';

const { isFromApp } = useApp();

type Modify<T, R> = Omit<T, keyof R> & R;
type NamedValue<T = object> = T & Record<string, unknown>;

type LanguageFile = {
    name: LanguageType;
    file: string;
}

export const i18nInstance = createI18n<false>({
    legacy: false,
    locale: LanguageType.EN,
    fallbackLocale: LanguageType.EN,
    messages: {
        [LanguageType.EN]: en,
    },
    silentTranslationWarn: __ENV_PROD__,
});

export const i18n = i18nInstance.global as Modify<typeof i18nInstance.global, {
    t(key: TranslateKeys): string;
    t(key: TranslateKeys, fallback: string): string;
    t(key: TranslateKeys, named: NamedValue): string;
    t(key: TranslateKeys, named: NamedValue, plural: number): string;
    t(key: TranslateKeys, list: unknown[]): string;

    locale: WritableComputedRef<LanguageType>;
    platformLanguageCookie: Ref<LanguageType>;
}>;

const languageFileMap: Array<LanguageFile> = [
    { name: LanguageType.EN, file: 'en' },
    { name: LanguageType.ID_ID, file: 'id-id' },
    { name: LanguageType.MY_MM, file: 'my-mm' },
    { name: LanguageType.TH_TH, file: 'th-th' },
    { name: LanguageType.VI_VN, file: 'vi-vn' },
    { name: LanguageType.ZH_CN, file: 'zh-cn' },
    { name: LanguageType.HI_IN, file: 'hi-in' },
    { name: LanguageType.TA_IN, file: 'ta-in' },
    { name: LanguageType.KO_KR, file: 'ko-kr' },
    { name: LanguageType.JA_JP, file: 'ja-jp' },
    { name: LanguageType.PT_BR, file: 'pt-br' },
    { name: LanguageType.KM_KH, file: 'km-kh' },
    { name: LanguageType.BN_BD, file: 'bn-bd' },
    { name: LanguageType.MS_MY, file: 'ms-my' },
];

export async function setI18nLanguage(langName = LanguageType.EN) {
    if (i18n.locale.value === LanguageType[langName]) {
        return;
    }

    const languageToLoad = languageFileMap.find(l => l.name === langName);
    if (languageToLoad) {
        await importLanguage(languageToLoad);
    }

    langName = languageToLoad ? languageToLoad.name : LanguageType.EN;
    applyI18nLanguage(langName);
}

async function importLanguage(language: LanguageFile) {
    const { default: messages } = await import(`../assets/i18n/${language.file}.json`);
    i18n.setLocaleMessage(language.name, messages);
}

function applyI18nLanguage(lang: LanguageType) {
    i18n.locale.value = lang;
    const langTag = lang.split('_')[0].toLowerCase();

    const html = document.querySelector('html');
    if (html) html.setAttribute('lang', langTag);

    if (isFromApp) {
        const thirtyDays = new Date(new Date().getTime() + (30 * 24 * 60 * 60 * 1000));
        Cookies.set('LanguageType', lang, { domain: getHost(), expires: thirtyDays });
    }
}

export function setLangFromCookie() {
    setI18nLanguage(getLangFromCookie());
}

export function getLangFromCookie() {
    const lang = Cookies.get('LanguageType') ?? LanguageType.EN;
    return Object.values(LanguageType).includes(lang as LanguageType) ? lang as LanguageType : LanguageType.EN;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export type TranslateKeys = keyof typeof en | (string & {});

export type TranslateValue = string | {
    asia: string;
    euro: string;
};

export type TranslateMessageType = {
    [key in TranslateKeys]: TranslateValue;
};
